// Бот на экранах: ссылка на бота, поддержка, «Подключить Telegram» (изменение telegram-bot).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { isApiError } from "../api/client";
import { createOrderTelegramLinks, createTelegramLink, getTelegramInfo } from "../api/endpoints";
import { ME_KEY, useMe } from "../app/queries";
import { useChannel } from "./channel";

export const TELEGRAM_INFO_KEY = ["telegram"] as const;

// Ссылка на бота и работает ли поддержка. Бот не запускался — ссылки нет, кнопок нет.
export function useTelegramInfo() {
  return useQuery({ queryKey: TELEGRAM_INFO_KEY, queryFn: getTelegramInfo, staleTime: 5 * 60_000 });
}

// Кнопка поддержки открывает чат с ботом; номер заказа бот получает из ссылки.
export function supportLink(botUrl: string, orderId?: string): string {
  return orderId === undefined ? botUrl : `${botUrl}?start=support_${orderId}`;
}

// Ссылка Telegram: в Mini App — средствами Telegram, Mini App не закрывается; на сайте —
// новой вкладкой.
export function useOpenTelegramLink(): (url: string) => void {
  const channel = useChannel();
  return (url: string) => {
    if (channel.kind === "telegram") {
      if (channel.webApp.openTelegramLink !== undefined) {
        channel.webApp.openTelegramLink(url);
      } else {
        channel.webApp.openLink(url);
      }
      return;
    }
    window.open(url, "_blank", "noopener");
  };
}

// Чат с нашим ботом. В Mini App ссылка Telegram с Bot API 7.0 Mini App не закрывает: он
// остаётся поверх чата, и покупатель не видит ответа бота. Поэтому Mini App закрывается сразу
// после перехода (support-topics, решение 8). На сайте — новая вкладка.
export function useOpenBotChat(): (url: string) => void {
  const channel = useChannel();
  const openTelegramLink = useOpenTelegramLink();
  return (url: string) => {
    openTelegramLink(url);
    if (channel.kind === "telegram") channel.webApp.close();
  };
}

// «Открыть бота» у заказа на сайте: ссылка с номером заказа — после «Старт» бот отвечает про
// него, а без подключённого Telegram ссылка ещё и соединяет учётки (telegram-continuity).
// Вкладка открывается сразу, из нажатия, ссылка подставляется, когда сервер ответил; не ответил —
// обычная ссылка на бота.
export function openOrderBot(orderId: string, fallbackUrl: string): void {
  const tab = window.open("", "_blank");
  const go = (url: string) => {
    if (tab === null) {
      window.open(url, "_blank", "noopener");
      return;
    }
    tab.opener = null;
    tab.location.href = url;
  };
  createOrderTelegramLinks(orderId)
    .then((links) => go(links.bot_url))
    .catch(() => go(fallbackUrl));
}

export type ConnectState = "idle" | "wait" | "done" | "expired";

// Ждём «Старт» в боте: раз в 3 секунды спрашиваем сервер, подключён ли Telegram.
export const CONNECT_POLL_MS = 3_000;
const CONNECT_ERROR_RU = "Не получилось получить ссылку на бота. Попробуй ещё раз.";

// Ссылка «Подключить Telegram». У заказа — с номером заказа и кодом: после «Старт» бот отвечает
// про этот заказ и даёт «Открыть заказ» (telegram-continuity). Без заказа — прежняя link_<код>.
async function createConnectLink(orderId: string | undefined): Promise<{ url: string; expiresAt: number }> {
  if (orderId === undefined) {
    const created = await createTelegramLink();
    return { url: created.url, expiresAt: Date.parse(created.expires_at) };
  }
  const links = await createOrderTelegramLinks(orderId);
  // Без кода — Telegram уже подключён: ссылке нечему устаревать.
  const expiresAt = links.expires_at === null ? Number.POSITIVE_INFINITY : Date.parse(links.expires_at);
  return { url: links.bot_url, expiresAt };
}

// «Подключить Telegram» на сайте: одноразовая ссылка на бота в новой вкладке, ожидание
// «Старт», «Telegram подключён» — экран узнаёт сам, без обновления страницы.
export function useTelegramConnect(orderId?: string) {
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const [state, setState] = useState<ConnectState>("idle");
  const [link, setLink] = useState<{ url: string; expiresAt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state !== "wait" || link === null) return undefined;
    const timer = window.setInterval(() => {
      if (Date.now() >= link.expiresAt) {
        setState("expired");
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    }, CONNECT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [state, link, queryClient]);

  useEffect(() => {
    if (state === "wait" && me?.telegram_linked) setState("done");
  }, [state, me]);

  const start = async () => {
    // Вкладка открывается сразу, из нажатия: иначе браузер заблокирует всплывающее окно.
    // Ссылку подставляем, когда сервер ответит.
    const tab = window.open("", "_blank");
    setBusy(true);
    try {
      const created = await createConnectLink(orderId);
      setLink(created);
      if (tab !== null) {
        tab.opener = null;
        tab.location.href = created.url;
      }
      setError(null);
      setState("wait");
    } catch (failure) {
      tab?.close();
      setError(isApiError(failure) ? failure.messageRu : CONNECT_ERROR_RU);
    } finally {
      setBusy(false);
    }
  };

  const openAgain = () => {
    if (link !== null) window.open(link.url, "_blank", "noopener");
  };

  return { state, start, openAgain, error, busy, linkUrl: link?.url ?? null };
}
